import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, LoaderCircle, Plus, Save, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { FieldError } from "../ui/field";
import { Input } from "../ui/input";
import useToast from "../../hooks/useToast";
import apiClient from "../../lib/utils/network-client";
import InlineFormFileUpload from "../file-upload/InlineFormFileUpload";
import SeoFields from "../seo/SeoFields";

const SECTION_LABELS = {
  editorial: "Editorial",
  review_article: "Review Article",
  original_article: "Original Article",
  case_report: "Case Report",
};

const createEmptyItem = () => ({
  _key: `journal-entry-${Date.now()}-${Math.random()}`,
  id: null,
  title: "",
  author: "",
  pdf: null,
  pdf_url: "",
  keepExisting: false,
});

const createEmptySections = () => ({
  editorial: [],
  review_article: [],
  original_article: [],
  case_report: [],
});

const EMPTY_FORM = {
  volume: "",
  number: "",
  duration: "",
  issue_pdf: null,
  issue_pdf_url: "",
  remove_issue_pdf: false,
  sections: createEmptySections(),
  meta_title: "",
  meta_description: "",
};

const JournalFormPage = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showErrorToast, showSuccessToast } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [isPageLoading, setIsPageLoading] = useState(mode === "edit");
  const [isSaving, setIsSaving] = useState(false);

  const isEditMode = mode === "edit";
  const pageTitle = useMemo(() => (isEditMode ? "Edit Journal Issue" : "Create Journal Issue"), [isEditMode]);

  useEffect(() => {
    if (!isEditMode) {
      setIsPageLoading(false);
      return;
    }

    const fetchJournal = async () => {
      setIsPageLoading(true);
      try {
        const response = await apiClient.get(`/journals/${id}`);
        const item = response.data?.data;
        if (!item) {
          showErrorToast("Journal not found");
          navigate("/journals");
          return;
        }

        const nextSections = createEmptySections();
        Object.keys(nextSections).forEach((section) => {
          const sectionItems = item.entries?.[section] || [];
          nextSections[section] = sectionItems.map((entry) => ({
                _key: `journal-entry-${entry.id}`,
                id: entry.id,
                title: entry.title || "",
                author: entry.author || "",
                pdf: null,
                pdf_url: entry.pdf_url || "",
                keepExisting: true,
              }));
        });

        setForm({
          volume: item.volume || "",
          number: item.number || "",
          duration: item.duration || "",
          issue_pdf: null,
          issue_pdf_url: item.issue_pdf_url || "",
          remove_issue_pdf: false,
          sections: nextSections,
          meta_title: item.meta_title || "",
          meta_description: item.meta_description || "",
        });
      } catch (err) {
        showErrorToast(err.response?.data?.message || "Failed to load journal");
        navigate("/journals");
      } finally {
        setIsPageLoading(false);
      }
    };

    fetchJournal();
  }, [id, isEditMode, navigate, showErrorToast]);

  const setSectionError = (section, message) => {
    setFormErrors((current) => ({ ...current, [section]: message || "" }));
  };

  const handleInputChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFormErrors((current) => ({ ...current, [field]: "" }));
  };

  const handleSectionItemChange = (section, index, field, value) => {
    setForm((current) => ({
      ...current,
      sections: {
        ...current.sections,
        [section]: current.sections[section].map((item, itemIndex) =>
          itemIndex === index ? { ...item, [field]: value } : item,
        ),
      },
    }));
    setSectionError(section, "");
  };

  const handleAddSectionItem = (section) => {
    setForm((current) => ({
      ...current,
      sections: {
        ...current.sections,
        [section]: [...current.sections[section], createEmptyItem()],
      },
    }));
  };

  const handleRemoveSectionItem = (section, index) => {
    setForm((current) => {
      const items = current.sections[section];
      const nextItems = items.filter((_, itemIndex) => itemIndex !== index);
      return {
        ...current,
        sections: {
          ...current.sections,
          [section]: nextItems,
        },
      };
    });
    setSectionError(section, "");
  };

  const handleEntryPdfChange = (section, index, file) => {
    setForm((current) => ({
      ...current,
      sections: {
        ...current.sections,
        [section]: current.sections[section].map((item, itemIndex) =>
          itemIndex === index
            ? {
                ...item,
                pdf: file,
                keepExisting: file ? false : Boolean(item.pdf_url),
              }
            : item,
        ),
      },
    }));
    setSectionError(section, "");
  };

  const handleRemoveExistingEntryPdf = (section, index) => {
    setForm((current) => ({
      ...current,
      sections: {
        ...current.sections,
        [section]: current.sections[section].map((item, itemIndex) =>
          itemIndex === index
            ? { ...item, pdf: null, pdf_url: "", keepExisting: false }
            : item,
        ),
      },
    }));
    setSectionError(section, "");
  };

  const handleIssuePdfChange = (file) => {
    setForm((current) => ({
      ...current,
      issue_pdf: file,
      remove_issue_pdf: file ? false : current.remove_issue_pdf,
    }));
    setFormErrors((current) => ({ ...current, content: "" }));
  };

  const handleRemoveExistingIssuePdf = () => {
    setForm((current) => ({
      ...current,
      issue_pdf: null,
      issue_pdf_url: "",
      remove_issue_pdf: true,
    }));
    setFormErrors((current) => ({ ...current, content: "" }));
  };

  const validateSection = (section) => {
    const items = form.sections[section] || [];
    for (const item of items) {
      if (!item.title.trim() || !item.author.trim()) {
        return `${SECTION_LABELS[section]} requires title and author for every row.`;
      }
      if (!isEditMode && !item.pdf) {
        return `${SECTION_LABELS[section]} requires a PDF for every row.`;
      }
      if (isEditMode && !item.keepExisting && !item.pdf) {
        return `${SECTION_LABELS[section]} requires a PDF for every row.`;
      }
    }
    return "";
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!form.volume.trim()) nextErrors.volume = "Volume is required.";
    if (!form.number.trim()) nextErrors.number = "Number is required.";
    if (!form.duration.trim()) nextErrors.duration = "Duration is required.";

    Object.keys(form.sections).forEach((section) => {
      const message = validateSection(section);
      if (message) {
        nextErrors[section] = message;
      }
    });

    const hasEntries = Object.values(form.sections).some((items) => items.length > 0);
    if (!form.issue_pdf && !form.issue_pdf_url && !hasEntries) {
      nextErrors.content = "Add a full issue PDF or at least one journal entry.";
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildCreatePayload = () => {
    const payload = new FormData();
    payload.append("volume", form.volume.trim());
    payload.append("number", form.number.trim());
    payload.append("duration", form.duration.trim());
    payload.append("meta_title", form.meta_title.trim());
    payload.append("meta_description", form.meta_description.trim());
    if (form.issue_pdf) {
      payload.append("issue_pdf", form.issue_pdf);
    }
    Object.entries(form.sections).forEach(([section, items]) => {
      items.forEach((item) => {
        payload.append(`${section}_titles`, item.title.trim());
        payload.append(`${section}_authors`, item.author.trim());
        payload.append(`${section}_pdfs`, item.pdf);
      });
    });

    return payload;
  };

  const buildUpdatePayload = () => {
    const payload = new FormData();
    payload.append("volume", form.volume.trim());
    payload.append("number", form.number.trim());
    payload.append("duration", form.duration.trim());
    payload.append("meta_title", form.meta_title.trim());
    payload.append("meta_description", form.meta_description.trim());
    if (form.issue_pdf) {
      payload.append("issue_pdf", form.issue_pdf);
    }
    if (form.remove_issue_pdf) {
      payload.append("remove_issue_pdf", "true");
    }

    const entriesPayload = {};
    Object.entries(form.sections).forEach(([section, items]) => {
      entriesPayload[section] = items.map((item) => ({
        id: item.id,
        title: item.title.trim(),
        author: item.author.trim(),
        keepExisting: item.keepExisting && !item.pdf,
      }));

      items.forEach((item) => {
        if (item.pdf) {
          payload.append(`${section}_pdfs`, item.pdf);
        }
      });
    });

    payload.append("entries_payload", JSON.stringify(entriesPayload));
    return payload;
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const payload = isEditMode ? buildUpdatePayload() : buildCreatePayload();

      if (isEditMode) {
        await apiClient.put(`/journals/${id}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast("Journal updated successfully");
      } else {
        await apiClient.post("/journals", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast("Journal created successfully");
      }

      navigate("/journals");
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to save journal");
    } finally {
      setIsSaving(false);
    }
  };

  const renderSection = (section) => (
    <div key={section} className="rounded-lg border border-slate-200 bg-slate-50/70 p-4">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-800">{SECTION_LABELS[section]}</h2>
          <p className="text-xs text-slate-500">Add title, author, and PDF entries.</p>
        </div>
        <Button type="button" variant="outline" className="rounded-lg" onClick={() => handleAddSectionItem(section)}>
          <Plus className="mr-2 h-4 w-4" />
          Add More
        </Button>
      </div>

      <div className="space-y-4">
        {form.sections[section].length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-500">
            No {SECTION_LABELS[section].toLowerCase()} entries added.
          </div>
        ) : null}
        {form.sections[section].map((item, index) => {
          const inputKey = item._key || `journal-entry-${item.id}`;
          return (
            <div key={inputKey} className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="grid gap-4 lg:grid-cols-[1fr_1fr_auto]">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Title</label>
                  <Input value={item.title} onChange={(event) => handleSectionItemChange(section, index, "title", event.target.value)} className="rounded-lg" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Author</label>
                  <Input value={item.author} onChange={(event) => handleSectionItemChange(section, index, "author", event.target.value)} className="rounded-lg" />
                </div>
                <div className="flex items-end justify-end">
                  <Button type="button" variant="outline" className="rounded-lg border-red-200 text-red-600 hover:bg-red-50" onClick={() => handleRemoveSectionItem(section, index)}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Remove
                  </Button>
                </div>
              </div>

              <div className="mt-4">
                <InlineFormFileUpload
                  label="PDF"
                  value={item.pdf}
                  onChange={(file) => handleEntryPdfChange(section, index, file)}
                  existingFiles={item.pdf_url ? [{
                    id: item.id,
                    name: `${item.title || "Journal entry"}.pdf`,
                    url: item.pdf_url,
                    mimeType: "application/pdf",
                  }] : []}
                  onRemoveExisting={() => handleRemoveExistingEntryPdf(section, index)}
                  accept={["application/pdf"]}
                  maxSizeBytes={10 * 1024 * 1024}
                  required
                  helperText="PDF — maximum 10 MB"
                />
              </div>
            </div>
          );
        })}
      </div>
      <FieldError className="mt-3">{formErrors[section]}</FieldError>
    </div>
  );

  if (isPageLoading) {
    return (
      <div className="rounded-lg border border-white/60 bg-white/80 p-8 shadow-sm">
        <div className="flex items-center gap-3 text-sm font-medium text-slate-500">
          <LoaderCircle className="h-4 w-4 animate-spin" />
          Loading journal editor...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] space-y-6">
      <div>
        <Button type="button" variant="ghost" className="-ml-3 mb-2 rounded-lg px-3 text-slate-500" onClick={() => navigate("/journals")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Journal Issues
        </Button>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">{pageTitle}</h1>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        <div className="rounded-lg border border-white/60 bg-white/80 p-6 shadow-sm backdrop-blur-xl">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Volume</label>
              <Input value={form.volume} onChange={(event) => handleInputChange("volume", event.target.value)} className={`rounded-lg ${formErrors.volume ? "border-red-300 focus-visible:ring-red-500" : ""}`} />
              <FieldError>{formErrors.volume}</FieldError>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Number</label>
              <Input value={form.number} onChange={(event) => handleInputChange("number", event.target.value)} className={`rounded-lg ${formErrors.number ? "border-red-300 focus-visible:ring-red-500" : ""}`} />
              <FieldError>{formErrors.number}</FieldError>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Duration</label>
              <Input value={form.duration} onChange={(event) => handleInputChange("duration", event.target.value)} className={`rounded-lg ${formErrors.duration ? "border-red-300 focus-visible:ring-red-500" : ""}`} />
              <FieldError>{formErrors.duration}</FieldError>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-white/60 bg-white/80 p-6 shadow-sm backdrop-blur-xl">
          <div className="space-y-2">
            <InlineFormFileUpload
              label="Full Issue PDF"
              optionalLabel="(optional)"
              value={form.issue_pdf}
              onChange={handleIssuePdfChange}
              existingFiles={form.issue_pdf_url ? [{
                name: `Journal volume ${form.volume || "issue"}.pdf`,
                url: form.issue_pdf_url,
                mimeType: "application/pdf",
              }] : []}
              onRemoveExisting={handleRemoveExistingIssuePdf}
              accept={["application/pdf"]}
              maxSizeBytes={10 * 1024 * 1024}
              error={formErrors.content}
              helperText="PDF — maximum 10 MB. Use this for the complete issue, or add individual entries below."
            />
          </div>
        </div>

        {Object.keys(form.sections).map(renderSection)}

        <SeoFields
          metaTitle={form.meta_title}
          metaDescription={form.meta_description}
          onChange={handleInputChange}
        />

        <div className="rounded-lg border border-white/60 bg-white/80 p-6 shadow-sm backdrop-blur-xl">
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" className="rounded-lg" onClick={() => navigate("/journals")}>
              Cancel
            </Button>
            <Button type="submit" className="rounded-lg" disabled={isSaving}>
              {isSaving ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              {isSaving ? "Saving..." : isEditMode ? "Update Journal" : "Create Journal"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default JournalFormPage;
